import { useState, type FormEvent } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import { ApiError, makeClient } from '../api/client'

type ForgotResponse = {
  message: string
  reset_url?: string
  expires_in_seconds?: number
}

export default function ForgotPassword() {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<ForgotResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  const client = makeClient(() => null)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setResult(null)
    setLoading(true)
    try {
      const data = await client.call<ForgotResponse>('/api/v1/auth/password-reset/request', {
        method: 'POST',
        body: { email: email.trim() },
        authed: false,
      })
      setResult(data)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
      }}
    >
      <Container maxWidth="xs">
        <Stack spacing={2}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main' }}>
              QEA FieldLens
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
              an Insight Delivered product
            </Typography>
          </Box>

          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              Reset your password
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Enter your account email. If it's registered we'll send you a reset link.
            </Typography>

            {result ? (
              <Stack spacing={2}>
                <Alert severity="success" variant="outlined">
                  {result.message}
                </Alert>
                {result.reset_url && (
                  <Alert severity="info" variant="outlined">
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Development mode:</strong> use this link (email delivery arrives in Package 7):
                    </Typography>
                    <Link component={RouterLink} to={result.reset_url}>
                      {result.reset_url}
                    </Link>
                    {result.expires_in_seconds !== undefined && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                        Expires in {Math.round(result.expires_in_seconds / 60)} minutes.
                      </Typography>
                    )}
                  </Alert>
                )}
                <Button
                  component={RouterLink}
                  to="/login"
                  variant="text"
                  size="small"
                >
                  Back to sign in
                </Button>
              </Stack>
            ) : (
              <form onSubmit={onSubmit} noValidate>
                <Stack spacing={2}>
                  <TextField
                    label="Email"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    required
                    fullWidth
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                  />

                  {error && (
                    <Alert severity="error" variant="outlined">
                      {error}
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    variant="contained"
                    color="secondary"
                    size="large"
                    disabled={loading || !email}
                  >
                    {loading ? 'Sending…' : 'Send reset link'}
                  </Button>

                  <Link component={RouterLink} to="/login" variant="body2" sx={{ textAlign: 'center' }}>
                    Back to sign in
                  </Link>
                </Stack>
              </form>
            )}
          </Paper>

          <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center' }}>
            Internal-only · Insight Delivered Ltd
          </Typography>
        </Stack>
      </Container>
    </Box>
  )
}
